import { useEffect } from "react";
import { useConnection, useSession } from "@/lib/store";
import { flushOutbox, useOutbox } from "@/lib/outbox";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Label, type Member } from "@/lib/api";
import { qk } from "@/lib/query";

/** Indicador de conexao online/reconnecting/offline (RF-UX-052). */
export function useOnline(): void {
  const setConn = useConnection((s) => s.setConn);
  const tenantId = useSession((s) => s.tenantId);
  const hydrate = useOutbox((s) => s.hydrate);

  useEffect(() => {
    if (tenantId) void hydrate(tenantId);
  }, [tenantId, hydrate]);

  // `navigator.onLine` NAO define mais o estado de conexao. O flag fica preso em
  // false no Chrome do Android e, como so era reavaliado nos eventos da janela
  // (que nesse estado nunca disparam), o app travava em "sem conexao" para sempre
  // mesmo com a rede boa. Quem manda agora e o WebSocket (`useRealtime`), que e
  // evidencia real de conectividade. O evento `online` serve so como gatilho
  // otimista para reavaliar; nada aqui rebaixa para offline.
  useEffect(() => {
    const onOnline = () => setConn("reconnecting");
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [setConn]);

  useEffect(() => {
    const onOnline = () => {
      if (tenantId) void flushOutbox(tenantId);
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [tenantId]);
}

export function useLabels() {
  const tenantId = useSession((s) => s.tenantId);
  return useQuery<Label[]>({
    queryKey: qk.labels(tenantId),
    queryFn: () => api.labels(),
    enabled: Boolean(tenantId),
  });
}

export function useMembers() {
  const tenantId = useSession((s) => s.tenantId);
  return useQuery<Member[]>({
    queryKey: qk.members(tenantId),
    queryFn: () => api.members(),
    enabled: Boolean(tenantId),
  });
}

export function useNotifications(tipo?: string) {
  const tenantId = useSession((s) => s.tenantId);
  return useQuery({
    queryKey: qk.notifications(tenantId, tipo),
    queryFn: () => api.notifications({ tipo }),
    enabled: Boolean(tenantId),
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) => api.markNotificationsRead(ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useChannels() {
  const tenantId = useSession((s) => s.tenantId);
  return useQuery({
    queryKey: qk.channels(tenantId),
    queryFn: () => api.channels(),
    enabled: Boolean(tenantId),
  });
}

export function usePushDiagnostics() {
  const tenantId = useSession((s) => s.tenantId);
  return useQuery({
    queryKey: qk.push(tenantId),
    queryFn: () => api.pushDiagnostics(),
    enabled: Boolean(tenantId),
    retry: false,
  });
}
